import type { Board } from "../board";
import type { Direction } from "../direction";
import { Event } from "../events/event";
import { EventType } from "../events/eventType";
import {
	type Bumpable,
	type Interactable,
	PRIORITY_DEFAULT,
	type ProcessableThing,
	type ProjectileHittable,
	type SomethingWithState,
} from "../interfaces";
import type { SimpleBoard } from "../levelgenerator/simpleBoard";
import { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import { Strings } from "../strings/strings";
import type { StockEntry } from "../vending/stockEntry";
import type { XY } from "../xy";
import { ZOrder } from "../zOrder";
import { Sound } from "./sounds/sound";
import { SoundId } from "./sounds/soundId";

export type VendingMode = "buy" | "sell" | "both";

export enum VendingMachineState {
	ON = "ON",
	OFF = "OFF",
}

export class VendingMachine
	extends PhysicalThing
	implements ProcessableThing, Interactable, ProjectileHittable, Bumpable, SomethingWithState
{
	static key = "VendingMachine";

	mode: VendingMode;
	offers: StockEntry[];
	state: VendingMachineState;

	constructor(xy: XY, mode: VendingMode = "both", state: VendingMachineState = VendingMachineState.ON) {
		super(xy, true);
		this.mode = mode;
		this.offers = [];
		this.state = state;
	}

	getZOrder(): number {
		return ZOrder.SCENARIO;
	}

	processTurn(board: Board): void {
		const player = board.player;
		if (!player) return;
		if (!this.xy.isInSquareRadius(player.xy, 1)) return;
		const text = Strings.get("prompt_useVendingMachine") ?? "prompt_useVendingMachine";
		board.emitPrompt(text);
	}

	getProcessPriority(): number {
		return PRIORITY_DEFAULT;
	}

	canInteract(): boolean {
		return true;
	}

	getState(): VendingMachineState {
		return this.state;
	}

	interact(_player: PhysicalThing): void {
		const board = this.board!;
		if (this.state === VendingMachineState.OFF) {
			board.addNonPhysicalThing(new Sound(SoundId.TERMINAL_ERROR, this.xy));
			board.addEvent(new Event(EventType.VENDING_MACHINE_NO_POWER));
			return;
		}
		board.requestOpenVendingMachine(this);
	}

	bump(_direction: Direction, author: PhysicalThing): boolean {
		if (!this.canInteract()) return false;
		this.interact(author);
		return true;
	}

	isHittable(): boolean {
		return true;
	}

	onProjectileHit(_projectile: unknown): void {
		this.board!.addNonPhysicalThing(new Sound(SoundId.METAL_BANG, this.xy));
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), mode: this.mode, offers: this.offers, state: this.state };
	}

	static deserialize(data: any): VendingMachine {
		const mode: VendingMode = data.mode ?? "both";
		const state: VendingMachineState = data.state ?? VendingMachineState.ON;
		const v = new VendingMachine(deserializeXY(data.xy), mode, state);
		v.offers = Array.isArray(data.offers) ? data.offers : [];
		return v;
	}

	static override initOnBoard(xy: XY, board: SimpleBoard, props?: Record<string, unknown>): PhysicalThing[] {
		const mode: VendingMode = (props?.mode as VendingMode) ?? "both";
		const state: VendingMachineState = (props?.state as VendingMachineState) ?? VendingMachineState.ON;
		const vm = new VendingMachine(xy, mode, state);
		board.putThing(vm);
		return [vm];
	}
}
